// File: frontend/src/hooks/useUrlPage.ts
import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

/** Query parameter holding the focused entry id, e.g. "?tanim=747". */
export const FOCUS_PARAM = "tanim";

/** Query parameter holding the entry page number, e.g. "?sayfa=2". */
export const PAGE_PARAM = "sayfa";

/**
 * Keeps the entry list's page number in the URL instead of in component state.
 *
 * Three things depend on this that component state cannot provide:
 *   - a page can be linked to and shared, and the browser's back button returns
 *     to the page the reader was on rather than to the top of the topic;
 *   - each page becomes its own address, so a topic with hundreds of entries
 *     contributes that many indexable URLs instead of one;
 *   - the page survives a refresh.
 *
 * Page 1 is written as a bare URL with no parameter rather than "?sayfa=1", so
 * a topic has exactly one canonical first-page address and search engines are
 * not handed two URLs with identical content.
 *
 * Anything malformed — "0", "-3", "abc", a missing value — is treated as page 1
 * rather than an error: these arrive from hand-edited or truncated links, and
 * showing the first page is what the reader expects.
 */
export function useUrlPage(): {
  page: number;
  setPage: (page: number) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(() => {
    const raw = searchParams.get(PAGE_PARAM);
    if (!raw) return 1;
    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed < 1) return 1;
    return parsed;
  }, [searchParams]);

  const setPage = useCallback((next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next <= 1) {
          params.delete(PAGE_PARAM);
        } else {
          params.set(PAGE_PARAM, String(next));
        }
        return params;
      },
      // A page change is a navigation the reader should be able to undo with
      // the back button, so this is pushed rather than replaced.
      { replace: false }
    );
  }, [setSearchParams]);

  return { page, setPage };
}

/**
 * Suffix appended to a page's <title> and canonical URL when the reader is
 * past the first page, so the pages are not all announced identically.
 * Returns empty strings for page 1, which stays the plain address.
 */
export function pageSuffixes(page: number): { titleSuffix: string; urlSuffix: string } {
  if (page <= 1) return { titleSuffix: "", urlSuffix: "" };
  return {
    titleSuffix: ` - sayfa ${page}`,
    urlSuffix: `?${PAGE_PARAM}=${page}`,
  };
}
/**
 * True when the address itself asks for a specific spot in the entry list:
 * either a page past the first ("?sayfa=4") or an entry anchor ("#entry-1499").
 *
 * This exists because of the collapsed-entries view (useCollapsedOldEntries).
 * That view renders only today's entries and hides the pagination controls
 * entirely, which is right for someone opening a topic to read it — but wrong
 * for someone arriving on a shared deep link. The requested page was fetched
 * and then never shown, and the anchored entry was not in the DOM for the
 * scroll to find, so every shared link looked like it opened page 1.
 *
 * Pages feed this into their collapse eligibility so an explicit request for a
 * page or an entry always wins over the default collapsed view.
 */
export function useIsEntryDeepLink(): boolean {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  return useMemo(() => {
    // "?tanim=" is the focused permalink view, which replaces the entry list
    // outright — leaving collapse eligible alongside it would run its
    // detection requests for nothing and render a second, contradictory
    // "{n} entry daha" bar above the focused one.
    if (searchParams.get(FOCUS_PARAM)) return true;
    const raw = searchParams.get(PAGE_PARAM);
    if (raw && Number(raw) > 1) return true;
    return location.hash.startsWith("#entry-");
  }, [searchParams, location.hash]);
}

/**
 * Reads "?tanim=747" — the id of the single entry a topic page should focus on.
 *
 * This is where an entry permalink lands. "/entry/747" resolves the topic and
 * then replaces itself with "/gundem/futbol--125?tanim=747", so the address
 * bar shows a readable topic URL while still pointing at one specific tanım.
 *
 * Returns null when the parameter is absent or not a positive integer, so a
 * hand-edited or truncated address falls back to the normal paginated view
 * instead of rendering an empty topic.
 */
export function useFocusEntryId(): number | null {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const raw = searchParams.get(FOCUS_PARAM);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
}

/**
 * Leaves the focused permalink view for the normal paginated list.
 *
 * Drops "?tanim", sets "?sayfa" to the page the focused entry sits on, and
 * puts the entry back in the hash so the list scrolls to it. The reader keeps
 * their place: the tanım they followed the link for is still on screen, now
 * surrounded by its neighbours.
 */
export function useExitFocus(): (page: number, entryId: number) => void {
  const [searchParams, setSearchParams] = useSearchParams();

  return useCallback((page: number, entryId: number) => {
    const next = new URLSearchParams(searchParams);
    next.delete(FOCUS_PARAM);
    if (page > 1) next.set(PAGE_PARAM, String(page));
    else next.delete(PAGE_PARAM);
    setSearchParams(next, { replace: false });
    // The hash is not part of URLSearchParams, so it is set separately. Doing
    // it after setSearchParams keeps both in the same history entry.
    window.location.hash = `entry-${entryId}`;
  }, [searchParams, setSearchParams]);
}